import { custodiansApiConnector } from 'apiConnector/secondary-market/custodiansApiConnector';
import classNames from 'classnames';
import { CompactSelector } from 'components/shared/compact-selector';
import { useFormatSalesCounterpartTooltip } from 'components/shared/compact-selector/use-format-tooltip';
import { useSalesCounterpartButton } from 'components/shared/compact-selector/use-toggle-button';
import { SelectSearch } from 'components/shared/SelectSearch/SelectSearch';
import type { Custodian } from 'models/custodian';
import { FC, useCallback } from 'react';
import type { Observable } from 'rxjs';
import { httpClient } from 'utils/http/httpClient';

import { CustodianResult } from './CustodianResult';

export type CustodiansSearchFieldProps = Readonly<{
  autoOpen?: boolean;
  selectedCustodian?: Custodian;
  isDisabled?: boolean;
  isReadonly?: boolean;
  onCustodianChanged: (client?: Custodian | null) => void;
  buttonClassName?: string;
  className?: string;
  defaultSearchedValue?: string;
  searchCustodians?: (text: string) => Observable<readonly Custodian[]>;
  onOpenChange?: (isOpen: boolean) => void;
}>;

export const CustodiansSearchField: FC<CustodiansSearchFieldProps> = ({
  autoOpen,
  selectedCustodian,
  buttonClassName,
  isDisabled = false,
  onCustodianChanged,
  className,
  defaultSearchedValue,
  searchCustodians,
  onOpenChange,
  isReadonly = false,
}) => {
  const fetchCustodians = useCallback(
    (text: string) => searchCustodians?.(text) ?? custodiansApiConnector(httpClient).searchCustodians(text),
    [searchCustodians],
  );
  const { label, className: defaultButtonClassName } = useSalesCounterpartButton(
    selectedCustodian,
    true,
    'form.Custodian.select',
  );
  const tooltip = useFormatSalesCounterpartTooltip(selectedCustodian);
  return (
    <CompactSelector
      isReadonly={isReadonly}
      autoOpen={autoOpen}
      hasClearButton={false}
      selectedValue={selectedCustodian ?? null}
      isDisabled={isDisabled}
      hasBorder
      className={className}
      tooltip={tooltip}
      onSelectedItemChanged={onCustodianChanged}
      onOpenChange={onOpenChange}
      renderContent={onSelectedChanged => (
        <SelectSearch
          autofocus
          disabled={isDisabled}
          defaultValue={defaultSearchedValue}
          getSearchResult={fetchCustodians}
          onValueSelected={onSelectedChanged}
          resultRenderer={CustodianResult}
          deduplicateComparator={(custodian1, custodian2) => custodian1.id === custodian2.id}
          e2e="custodians-selector-search-input"
        />
      )}
      e2ePrefix="custodians"
      renderButton={(ref, onClick) => (
        <CompactSelector.ToggleButton
          onClick={onClick}
          ref={ref}
          isReadonly={isReadonly}
          className={classNames(defaultButtonClassName, buttonClassName)}
          disabled={isDisabled}
          e2ePrefix="custodians"
          id="custodians"
          label={label}
        />
      )}
    ></CompactSelector>
  );
};

CustodiansSearchField.displayName = 'CustodiansSearchField';
